<template>
  <div class="chart-wrapper">
    <b-spinner v-if="loading" variant="secondary" small class="spinner" />
    <div class="chart-container"><div id="line-chart"></div></div>
  </div>
</template>

<script>
import Vue from 'vue';
import { formatWideInt } from '@/utils/filters.js';

export default {
  props: {
    /* Example
    [
      {
        "name": "Views",
        "data": [[datetime, value], [1532995200, 86840086], [1533081600, 87498962]]
      },
      {
        "name": "Clicks",
        "data": [[1532995200, 162543], [1533081600, 141440]]
      }
    ]
    */
    data: {
      type: Array,
      required: true
    },
    timeFormatter: {
      type: Function,
      default: ({ value }) => Vue.prototype.$date.format(value, 'DD MMM, YYYY')
    },
    showLegend: {
      type: Boolean,
      default: false
    },
    verticalLabels: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    formatter: {
      type: Function,
      default: val => formatWideInt(val)
    },
    maxVerticalLimit: {
      type: Number,
      default: null
    },
    showMarker: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      Highcharts: null,
      chart: null,
      legend: null
    };
  },

  methods: {
    generateYAxisOptions(length) {
      const yAxisOptions = [];
      const tickInterval = this.maxVerticalLimit
        ? Math.round(this.maxVerticalLimit / 4)
        : null;

      for (let i = 0; i < length; i++) {
        let item = {
          title: {
            text: ''
          },
          opposite: i % 2,
          gridLineColor: this.$config.chart.yAxis.gridLineColor,
          tickInterval,
          max: this.maxVerticalLimit,
          labels: {
            enabled: this.verticalLabels,
            align: 'left',
            x: 0,
            y: -2,
            formatter: function() {
              return this.value;
            },
            style: {
              color: this.$config.chart.yAxis.labels.style.color
            }
          }
        };
        yAxisOptions.push(item);
      }

      return yAxisOptions;
    },
    initChart(series) {
      const self = this;
      const margin = this.showLegend ? [10, 0, 90, 0] : [10, 0, 40, 0];

      if (this.legend === null) {
        this.legend = {};
        series.forEach(item => {
          this.legend[item.name] = true;
        });
      } else {
        series.forEach(item => {
          item.visible = this.legend[item.name];
        });
      }

      const options = {
        chart: {
          height: 400,
          type: 'spline',
          margin,
          animation: false
        },
        title: {
          text: ''
        },
        colors: this.$config.chart.colors,
        xAxis: {
          type: 'datetime',
          minPadding: 0,
          maxPadding: 0,
          tickColor: this.$config.chart.xAxis.tickColor,
          tickPosition: 'inside',
          labels: {
            style: {
              textTransform: 'capitalize',
              fontWeight: 300,
              color: this.$config.chart.xAxis.labels.style.color,
              fontSize: '12px'
            },
            formatter: this.timeFormatter,
            y: 30
          },
          crosshair: true
        },
        yAxis: this.generateYAxisOptions(series.length),
        tooltip: {
          formatter: function() {
            let s = new String();

            this.points.forEach(item => {
              s += `
              <i>${self.timeFormatter({ value: item.x })}</i>
              <label>
                <small
                  class='point-color'
                  style='background: ${item.color}'>
                </small>
                <span>${item.series.name}: ${self.formatter(item.y)}</span>
              </label>`;
            });
            return s;
          },
          backgroundColor: null,
          borderWidth: 0,
          shadow: false,
          useHTML: true,
          shared: true
        },
        plotOptions: {
          column: {
            pointPadding: 0,
            borderWidth: 0
          },
          series: {
            animation: false,
            lineWidth: 2,
            pointPadding: 0,
            groupPadding: 0,
            shadow: {
              color: this.$config.chart.plotOptions.series.shadow.color,
              width: 1.5,
              opacity: 1,
              offsetY: 2
            },
            marker: {
              enabled: this.showMarker,
              fillColor: this.$config.chart.plotOptions.series.marker.color,
              radius: 5,
              lineWidth: 2,
              lineColor: null,
              states: {
                hover: {
                  enabled: true
                }
              }
            },
            events: {
              legendItemClick: item => {
                this.legend[item.target.getName()] = !item.target.visible;
              }
            }
          }
        },
        legend: {
          enabled: this.showLegend,
          itemMarginBottom: -7
        },
        credits: {
          enabled: false
        },
        series
      };

      if (this.chart) {
        this.chart.destroy();
      }
      this.chart = this.Highcharts.chart('line-chart', options);
    },

    prepareData(data) {
      const seriesOptions = [];
      data.forEach((dimension, index) => {
        seriesOptions.push({
          name: dimension.name,
          yAxis: index,
          data: [...dimension.data]
        });
      });
      return seriesOptions;
    }
  },

  async created() {
    this.Highcharts = await import(/* webpackChunkName: "chart" */ 'highcharts');
    this.initChart(this.prepareData(this.data));
    // if (this.loading) this.chart.showLoading();
  },

  watch: {
    data(val) {
      if (this.chart === null) return;
      for (let i = this.chart.series.length - 1; i > -1; i--) {
        this.chart.series[i].remove();
      }

      this.prepareData(val).forEach(el => this.chart.addSeries(el));
      this.chart.update({
        plotOptions: {
          marker: {
            enabled: this.showMarker
          }
        },
        yAxis: this.generateYAxisOptions(this.chart.series.length)
      });
    }
    // loading(val) {
    //   if (val) this.chart.showLoading();
    //   else this.chart.hideLoading();
    // }
  }
};
</script>

<style lang="scss" scoped>
.spinner {
  position: absolute;
  top: 10px;
  right: 22px;
  z-index: 2;
}
</style>
